/**
 * <vv-quick-add> — reusable quick-add modal driven entirely by data-* attributes
 * on the trigger buttons (no product-page fetch). Mirrors the homepage carousel's
 * quick-add UX so the search results page shows the same modal.
 *
 * Expects, somewhere inside it:
 *   - one or more `[data-vv-quick-add]` trigger buttons carrying data-title,
 *     data-vendor, data-price, data-description, data-url, data-image,
 *     data-sizes, data-unavailable-sizes, data-variant-id
 *   - one `[data-vv-product-modal]` <dialog> (see snippets/vv-quick-add-modal.liquid)
 *
 * Clicks are delegated, so trigger buttons added after an AJAX re-render (e.g.
 * Horizon's filtered search results) keep working.
 *
 * Guarded against duplicate injection: several sections (search-results,
 * vv-recently-viewed, main-collection, header-actions) inject this asset
 * because each can stand alone on a page. When two of them co-exist (e.g. the
 * search page renders both search-results and vv-recently-viewed) the script
 * loads twice, and a second top-level `class VVQuickAdd` declaration throws
 * `Identifier 'VVQuickAdd' has already been declared`. The early-return below
 * makes the second load a no-op without needing to dedupe the <script> tags.
 */
if (!customElements.get('vv-quick-add')) {
class VVQuickAdd extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('[data-vv-product-modal]');
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('[data-vv-quick-add]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        this.openQuickAdd(button);
      },
      { signal }
    );

    this.querySelector('[data-vv-modal-close]')?.addEventListener('click', () => this.closeQuickAdd(), { signal });
    this.modal?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.modal) this.closeQuickAdd();
      },
      { signal }
    );
    this.querySelector('[data-vv-modal-add]')?.addEventListener('click', () => this.addSelectedVariant(), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  openQuickAdd(button) {
    if (!this.modal) return;

    const dataset = button.dataset;
    const title = dataset.title || '';

    // Variant-aware state (mirrors vv-pdp-main.js): the trigger carries the
    // product's variants JSON + the colour/size option positions, so the modal
    // can resolve the exact variant for the selected combination and mark
    // sold-out values. Triggers without data-variants (manual block content)
    // fall back to the legacy single-variant behaviour.
    this.variants = this.parseVariants(dataset.variants);
    this.colorPosition = parseInt(dataset.colorPosition || '0', 10) || 0;
    this.sizePosition = parseInt(dataset.sizePosition || '0', 10) || 0;
    this.fallbackVariantId = dataset.variantId || '';
    this.lineProperties = this.parseProperties(dataset.properties);
    this.legacyUnavailableSizes = new Set(this.parseList(dataset.unavailableSizes));

    const initial = this.variants.find((variant) => variant.available) || this.variants[0];
    this.selectedOptions = initial ? initial.options.slice() : [];
    this.modalTitle = title;
    this.currentModalImage = dataset.image || '';

    this.setText('[data-vv-modal-title]', title);
    this.setText('[data-vv-modal-product]', title);
    this.setText('[data-vv-modal-vendor]', dataset.vendor || '');
    this.setText('[data-vv-modal-price]', dataset.price || '');
    this.setText('[data-vv-modal-description]', dataset.description || '');

    const detailsLink = this.querySelector('[data-vv-modal-details]');
    if (detailsLink) detailsLink.href = dataset.url || '#';

    this.renderModalImage(dataset.image || '', title);
    this.renderColors(dataset.colors || '');
    this.renderSizes(this.parseList(dataset.sizes));
    this.updateModalState();

    this.modal.showModal();
  }

  closeQuickAdd() {
    this.modal?.close();
  }

  renderModalImage(image, title) {
    const imageWrap = this.querySelector('[data-vv-modal-image]');
    if (!imageWrap) return;

    imageWrap.innerHTML = '';

    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = title;
      img.loading = 'lazy';
      imageWrap.append(img);
      return;
    }

    const placeholder = document.createElement('span');
    placeholder.className = 'vv-qa__modal-placeholder';
    imageWrap.append(placeholder);
  }

  renderColors(raw) {
    const colorWrap = this.querySelector('[data-vv-modal-colors]');
    if (!colorWrap) return;

    colorWrap.innerHTML = '';

    const colors = raw
      .split(';;')
      .map((item) => item.trim())
      .filter(Boolean);

    // Hide the whole Color field when the product has no colour option.
    const field = colorWrap.closest('[data-vv-modal-color-field]');
    if (field) field.hidden = colors.length === 0;

    colors.forEach((entry) => {
      const [name, background] = entry.split('||');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `vv-qa__color-button vv-swatch-${this.handleize(name)}`;
      button.dataset.value = name || '';
      button.title = name || '';
      button.setAttribute('aria-label', name || '');

      // PDP-parity colour resolution: the Liquid-resolved background (native
      // swatch colour -> metaobject color_code -> swatch/metaobject image)
      // wins; otherwise try the value NAME as a CSS colour (invalid names are
      // silently dropped, leaving the global .vv-swatch-* class to colour it).
      if (background) {
        button.style.background = background;
      } else if (name) {
        button.style.backgroundColor = name.toLowerCase();
      }

      button.addEventListener('click', () => {
        if (this.colorPosition) {
          this.selectedOptions[this.colorPosition - 1] = name;
          this.updateModalState();
          return;
        }
        colorWrap.querySelectorAll('.vv-qa__color-button').forEach((item) => item.classList.remove('is-selected'));
        button.classList.add('is-selected');
        this.updateOptionLabels();
      });

      colorWrap.append(button);
    });

    // Legacy fallback (no variants data): keep the first chip visually selected.
    if (!this.variants.length && colorWrap.firstElementChild) {
      colorWrap.firstElementChild.classList.add('is-selected');
    }
  }

  renderSizes(sizes) {
    const sizeWrap = this.querySelector('[data-vv-modal-sizes]');
    if (!sizeWrap) return;

    sizeWrap.innerHTML = '';

    // Hide the whole Size field when the product has no size option.
    const field = sizeWrap.closest('[data-vv-modal-size-field]');
    if (field) field.hidden = sizes.length === 0;

    sizes.forEach((size, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'vv-qa__size-button';
      button.dataset.value = size;
      button.textContent = size;
      button.disabled = this.legacyUnavailableSizes.has(size);

      if (!this.variants.length && index === 0 && !button.disabled) {
        button.classList.add('is-selected');
      }

      button.addEventListener('click', () => {
        if (this.sizePosition) {
          this.selectedOptions[this.sizePosition - 1] = size;
          this.updateModalState();
          return;
        }
        sizeWrap.querySelectorAll('.vv-qa__size-button').forEach((item) => item.classList.remove('is-selected'));
        button.classList.add('is-selected');
        this.updateOptionLabels();
      });

      sizeWrap.append(button);
    });
  }

  /* Sync swatch/size selection + sold-out states and resolve the variant for
     the current combination (mirrors vv-pdp-main.js #updateState). */
  updateModalState() {
    const selectedColor = this.colorPosition ? this.selectedOptions[this.colorPosition - 1] : null;
    const selectedSize = this.sizePosition ? this.selectedOptions[this.sizePosition - 1] : null;

    if (this.variants.length) {
      this.querySelectorAll('.vv-qa__color-button').forEach((btn) => {
        const soldOut = !this.valueHasStock(this.colorPosition, btn.dataset.value);
        btn.classList.toggle('is-selected', btn.dataset.value === selectedColor);
        btn.classList.toggle('is-oos', soldOut);
        btn.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
      });

      this.querySelectorAll('.vv-qa__size-button').forEach((btn) => {
        btn.classList.toggle('is-selected', btn.dataset.value === selectedSize);
        btn.disabled = !this.availableFor(this.sizePosition, btn.dataset.value);
      });
    }

    this.updateModalImage();
    this.updateOptionLabels();

    const addButton = this.querySelector('[data-vv-modal-add]');
    if (!addButton) return;

    if (!this.variants.length) {
      addButton.dataset.variantId = this.fallbackVariantId;
      addButton.disabled = false;
      addButton.textContent = addButton.dataset.defaultLabel || 'Add to Cart';
      return;
    }

    const variant = this.findVariant();
    if (variant && variant.available) {
      addButton.dataset.variantId = String(variant.id);
      addButton.disabled = false;
      addButton.textContent = addButton.dataset.defaultLabel || 'Add to Cart';
    } else {
      addButton.dataset.variantId = '';
      addButton.disabled = true;
      addButton.textContent = addButton.dataset.soldOutLabel || 'Sold Out';
    }
  }

  findVariant() {
    return this.variants.find((variant) => variant.options.every((opt, i) => opt === this.selectedOptions[i]));
  }

  /* Swap the modal image to the selected variant's featured media (falling
     back to any variant of the selected colour, so sold-out combos still
     preview their colour) — mirrors the PDP gallery swap. */
  updateModalImage() {
    const variant = this.findVariant();
    let image = variant && variant.image;
    if (!image && this.colorPosition) {
      const selectedColor = this.selectedOptions[this.colorPosition - 1];
      const match = this.variants.find((v) => v.options[this.colorPosition - 1] === selectedColor && v.image);
      image = match && match.image;
    }
    if (image && image !== this.currentModalImage) {
      this.currentModalImage = image;
      this.renderModalImage(image, this.modalTitle || '');
    }
  }

  /* Echo the chosen value next to the field label ("Color: Olive") — mirrors
     the PDP option labels. Variant path reads selectedOptions; the legacy
     path (no variants data) reads the .is-selected chip. */
  updateOptionLabels() {
    const colorChip = this.querySelector('.vv-qa__color-button.is-selected');
    const sizeChip = this.querySelector('.vv-qa__size-button.is-selected');
    const color = this.colorPosition ? this.selectedOptions[this.colorPosition - 1] : colorChip && colorChip.dataset.value;
    const size = this.sizePosition ? this.selectedOptions[this.sizePosition - 1] : sizeChip && sizeChip.dataset.value;
    this.setText('[data-vv-modal-color-value]', color ? ': ' + color : '');
    this.setText('[data-vv-modal-size-value]', size ? ': ' + size : '');
  }

  /* A size is offered when some available variant carries it together with the
     OTHER currently-selected options (i.e. availability follows the colour). */
  availableFor(position, value) {
    if (!position) return true;
    return this.variants.some((variant) => {
      if (variant.options[position - 1] !== value) return false;
      const matchesOthers = variant.options.every((opt, i) => {
        if (i === position - 1) return true;
        if (this.selectedOptions[i] === undefined) return true;
        return opt === this.selectedOptions[i];
      });
      return matchesOthers && variant.available;
    });
  }

  /* True when ANY variant carrying this option value is in stock — flags
     colours that are entirely sold out, before they're clicked. */
  valueHasStock(position, value) {
    if (!position) return true;
    return this.variants.some((variant) => variant.options[position - 1] === value && variant.available);
  }

  parseVariants(raw) {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  /* VV-233: line item properties (the final-sale note) from the trigger's
     data-properties JSON, sent with /cart/add.js so checkout shows them.
     Returns null when absent, malformed or not a non-empty object - never throws. */
  parseProperties(raw) {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      const isObject = parsed && typeof parsed === 'object' && !Array.isArray(parsed);
      return isObject && Object.keys(parsed).length > 0 ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  handleize(value = '') {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  async addSelectedVariant() {
    const addButton = this.querySelector('[data-vv-modal-add]');
    const variantId = addButton?.dataset.variantId;

    if (!addButton) return;

    if (!variantId) {
      this.closeQuickAdd();
      return;
    }

    addButton.disabled = true;
    addButton.textContent = addButton.dataset.loadingLabel || 'Adding...';

    // Section ids for the cart drawer / line items, so they can re-render
    // without a full page reload (Section Rendering API).
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    try {
      const body = { id: Number(variantId), quantity: 1 };
      if (this.lineProperties) body.properties = this.lineProperties;
      if (sectionIds.length) body.sections = [...new Set(sectionIds)].join(',');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Add to cart failed');

      const data = await response.json();

      // Mirror Horizon's CartAddEvent so the cart icon count and cart drawer
      // update live (cart-icon / cart-drawer listen on `cart:update`).
      const event = new Event('cart:update', { bubbles: true });
      event.detail = {
        resource: data,
        sourceId: 'vv-quick-add',
        data: {
          source: 'product-form-component',
          itemCount: 1,
          sections: data.sections,
        },
      };
      document.dispatchEvent(event);

      addButton.textContent = addButton.dataset.addedLabel || 'Added';
      setTimeout(() => this.closeQuickAdd(), 600);
    } catch (error) {
      addButton.disabled = false;
      addButton.textContent = addButton.dataset.errorLabel || 'Try again';
    }
  }

  setText(selector, text) {
    const element = this.querySelector(selector);
    if (element) element.textContent = text;
  }

  parseList(value = '') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
}

customElements.define('vv-quick-add', VVQuickAdd);
} // end if (!customElements.get('vv-quick-add'))
